import { Button } from '../../components/Button';
import { Plus, Wand2 } from '../../icons/lucide';
import { View } from '../../primitives/View';
import { SurfaceEmptyState } from '../SurfaceEmptyState';
import { flashcardsEmptyStateActions } from './flashcards-empty-state-actions';
import { FlashcardsEmptyArtwork } from './FlashcardsEmptyArtwork';
import type { FlashcardsEmptyStateProps } from './FlashcardsEmptyState.types';

/**
 * The deck's "no cards yet" card — the flashcards face of
 * {@link SurfaceEmptyState}, and the sibling of `QuizzesEmptyState`: same card,
 * same type ramp, a short heading, ONE line of copy, then the actions.
 *
 * It replaced three hand-built versions: the phone/iPad "Get started with your
 * first flashcards!" card (a two-sentence tutorial pointing at ✨/⊕ buttons that
 * are not in the pane, plus two full-size buttons), and web's one-liner
 * markup. The designer's note was "it can be as simple as the quiz one".
 *
 * Its default ARTWORK is the still {@link FlashcardsEmptyArtwork}. Each app
 * passes its animated `FlashcardsEmptyRiveArt` (the same picture as one Rive
 * file, `flashcards-empty.riv`) through `artwork`, sized for its box.
 *
 * What this owns is the ACTION rule (see `flashcardsEmptyStateActions`): one
 * primary button, with "Write your own" quiet under Generate and primary when
 * it is the only way in. Labels arrive pre-translated (the DS carries no i18n).
 */
export function FlashcardsEmptyState({
  artwork,
  title,
  description,
  generate,
  generateSlot,
  writeOwn,
  className,
  testID,
}: FlashcardsEmptyStateProps) {
  const plan = flashcardsEmptyStateActions({
    hasGenerate: !!(generateSlot || generate),
    hasWriteOwn: !!writeOwn,
  });

  const generateNode = !plan.showGenerate
    ? null
    : (generateSlot ?? (
        <Button
          variant="primary"
          size="l"
          fullWidth
          leftIcon={generate?.icon ?? Wand2}
          disabled={generate?.disabled}
          onPress={generate?.onPress}
          testID={generate?.testID}>
          {generate?.label}
        </Button>
      ));

  const writeNode =
    writeOwn && plan.writeOwn ? (
      <Button
        // Ghost under a Generate (the designer's "quiet secondary"), the
        // primary when there is nothing to generate from.
        variant={plan.writeOwn === 'primary' ? 'primary' : 'tertiary'}
        size="l"
        fullWidth
        leftIcon={writeOwn.icon ?? Plus}
        disabled={writeOwn.disabled}
        onPress={writeOwn.onPress}
        testID={writeOwn.testID}>
        {writeOwn.label}
      </Button>
    ) : null;

  return (
    <SurfaceEmptyState
      artwork={artwork === undefined ? <FlashcardsEmptyArtwork /> : artwork}
      title={title}
      description={description}
      action={
        generateNode || writeNode ? (
          <View className="w-full flex-col items-stretch gap-2">
            {generateNode}
            {writeNode}
          </View>
        ) : undefined
      }
      className={className}
      testID={testID}
    />
  );
}
(FlashcardsEmptyState as { displayName?: string }).displayName = 'FlashcardsEmptyState';
